import { cx } from "@unizpay/ui";
import { Check, ChevronsUpDown, Plus } from "lucide-react";
import { useNavigate } from "react-router";
import { useProfile } from "../lib/profile";
import { whenSafeToLeave } from "../lib/unsavedChanges";
import { Menu, menuItemClass } from "./Menu";
import { ProfileAvatar } from "./ProfileAvatar";
import { RoleBadge } from "./RoleBadge";

/**
 * The active profile at the top of the sidebar, opening a menu of all the user's profiles (with
 * their role in each) and "Create profile". `onSwitch` lets the mobile drawer close itself.
 */
export function ProfileSwitcher({ onSwitch }: { onSwitch?: () => void }) {
  const { profiles, profile, switchProfile } = useProfile();
  const navigate = useNavigate();

  return (
    <Menu
      label={`Switch profile (current: ${profile.name})`}
      align="left"
      triggerClassName="flex h-10 w-full min-w-0 items-center gap-2.5 rounded-md border border-line px-2.5 text-left hover:bg-subtle aria-expanded:bg-subtle"
      trigger={
        <>
          <ProfileAvatar name={profile.name} />
          <span className="min-w-0 flex-1 truncate text-sm font-medium">{profile.name}</span>
          <ChevronsUpDown aria-hidden="true" className="size-3.5 shrink-0 text-fg-subtle" />
        </>
      }
      panelClassName="w-full min-w-60"
    >
      {(close) => (
        <>
          <p className="px-3 pt-1 pb-1.5 text-xs font-medium text-fg-muted">Profiles</p>
          {profiles.map((p) => {
            const current = p.id === profile.id;
            return (
              <button
                key={p.id}
                type="button"
                role="menuitemradio"
                aria-checked={current}
                tabIndex={-1}
                onClick={() => {
                  close();
                  if (current) return;
                  whenSafeToLeave(() => {
                    switchProfile(p.id);
                    navigate("/dashboard");
                    onSwitch?.();
                  });
                }}
                className={menuItemClass}
              >
                <ProfileAvatar name={p.name} />
                <span className="min-w-0 flex-1 truncate font-medium">{p.name}</span>
                <RoleBadge role={p.role} />
                <Check
                  aria-hidden="true"
                  className={cx("size-4 shrink-0 text-accent", !current && "invisible")}
                />
              </button>
            );
          })}
          <hr className="my-1 border-line" />
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            onClick={() => {
              close(false);
              onSwitch?.();
              navigate("/profiles/new");
            }}
            className={menuItemClass}
          >
            <Plus aria-hidden="true" className="size-4 text-fg-muted" />
            Create profile
          </button>
        </>
      )}
    </Menu>
  );
}
